import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PREF_DEFAULTS } from '@maker-toolchest/shared';
import { useUserPreference, useSetUserPreference } from './hooks';
import { preferencesCache } from '@/lib/preferencesCache';
import { useAuth } from '@/hooks/useAuth';
import type { ViewMode } from '@/components/ViewModeToggle';

/**
 * Hook to manage view mode with the following priority:
 * 1. URL parameter (?view=grid|list|table)
 * 2. User preference (persisted in database)
 * 3. Default value from PREF_DEFAULTS
 *
 * When the URL parameter changes, it automatically saves to user preferences.
 * When there's no URL parameter, it loads from user preferences.
 *
 * @param page - Page identifier (e.g., 'jobs', 'models', 'printers', 'spools')
 * @returns Object with viewMode and setViewMode function
 */
export function useViewMode(page: 'jobs' | 'models' | 'printers' | 'spools') {
  const { user } = useAuth();
  const userId = user?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const preferenceKey = `view.${page}.mode` as const;

  // Get default from centralized defaults
  const defaultMode = PREF_DEFAULTS.VIEW[`${page.toUpperCase()}_MODE` as keyof typeof PREF_DEFAULTS.VIEW] as ViewMode;

  // Try to get from cache first for instant render
  const cachedMode = userId ? preferencesCache.get(userId, preferenceKey) : undefined;

  // Get view mode from user preferences
  const { data: savedViewMode } = useUserPreference<ViewMode>(
    preferenceKey,
    cachedMode ?? defaultMode
  );
  const setUserPreference = useSetUserPreference();

  // Get view mode from URL or use saved preference
  const urlViewMode = searchParams.get('view') as ViewMode | null;
  const viewMode = urlViewMode || savedViewMode || defaultMode;

  // Save to preferences when URL changes (user explicitly changed view)
  useEffect(() => {
    if (urlViewMode && urlViewMode !== savedViewMode) {
      setUserPreference.mutate(
        { key: preferenceKey, value: urlViewMode },
        {
          onSuccess: () => {
            // Update cache on successful save
            if (userId) {
              preferencesCache.set(userId, preferenceKey, urlViewMode);
            }
          },
        }
      );
    }
  }, [urlViewMode, savedViewMode, preferenceKey, setUserPreference, userId]);

  // Function to change view mode (updates URL, which triggers preference save)
  const setViewMode = (mode: ViewMode) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', mode);
    setSearchParams(newParams);
  };

  return {
    viewMode,
    setViewMode,
  };
}
